import { useEffect, useMemo, useState } from "react";
import { listStages } from "../../content/stages.js";
import { AnalyticsMap, METRIC_LABELS, type AnalyticsMetric } from "./AnalyticsMap.js";
import { DailyChart, type DailyMetric } from "./DailyChart.js";
import { HourlyChart, type HourlyMetric } from "./HourlyChart.js";
import { loadAnalyticsSummary, loginAnalytics, type AnalyticsSummary } from "./analyticsApi.js";

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const PERIOD_PRESETS = [
  { days: 1, label: "直近24時間", purpose: "最近の動き" },
  { days: 7, label: "直近7日間", purpose: "日ごとの変化" },
  { days: 30, label: "直近30日間", purpose: "長期の傾向" },
];

function displayDate(ms: number): string {
  return inputDate(ms).replace("T", " ").replaceAll("-", "/");
}

function inputDate(ms: number): string {
  return new Date(ms + JST_OFFSET_MS).toISOString().slice(0, 16);
}

function parseInputDate(value: string): number | null {
  const time = Date.parse(`${value}:00+09:00`);
  return Number.isFinite(time) ? time : null;
}

function initialRange() {
  const to = Date.now();
  return { from: to - 24 * 60 * 60 * 1000, to };
}

export function AnalyticsScreen() {
  const initial = useMemo(initialRange, []);
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState(inputDate(initial.from));
  const [to, setTo] = useState(inputDate(initial.to));
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [stageId, setStageId] = useState(listStages()[0].id);
  const [metric, setMetric] = useState<AnalyticsMetric>("total");
  const [hourlyMetric, setHourlyMetric] = useState<HourlyMetric>("stages");
  const [dailyMetric, setDailyMetric] = useState<DailyMetric>("stages");
  const [loading, setLoading] = useState(false);
  const [activePreset, setActivePreset] = useState<number | null>(1);

  const loadRange = async (fromValue: string, toValue: string, preset: number | null = null) => {
    const range = { from: parseInputDate(fromValue), to: parseInputDate(toValue) };
    if (!range.from || !range.to || range.from >= range.to) {
      setError("開始日時と終了日時を正しく指定してください。");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setSummary(await loadAnalyticsSummary(range.from, range.to));
      setActivePreset(preset);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "集計の取得に失敗しました。";
      setError(message);
      if (message === "unauthorized") setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };
  const load = () => loadRange(from, to);
  const applyPreset = (days: number) => {
    const end = Date.now();
    const start = end - days * 24 * 60 * 60 * 1000;
    const fromValue = inputDate(start);
    const toValue = inputDate(end);
    setFrom(fromValue);
    setTo(toValue);
    void loadRange(fromValue, toValue, days);
  };

  useEffect(() => { if (authenticated && !summary) void loadRange(from, to, 1); }, [authenticated, summary]);
  useEffect(() => {
    let disposed = false;
    void loadAnalyticsSummary(initial.from, initial.to)
      .then((value) => {
        if (!disposed) {
          setSummary(value);
          setAuthenticated(true);
        }
      })
      .catch(() => undefined);
    return () => { disposed = true; };
  }, [initial]);

  if (!authenticated) {
    return (
      <main className="analytics-login">
        <form className="analytics-login-card" onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          void loginAnalytics(password).then(() => setAuthenticated(true)).catch((cause: unknown) => setError(cause instanceof Error ? cause.message : "認証に失敗しました。"));
        }}>
          <p className="analytics-eyebrow">PRIVATE ANALYTICS</p>
          <h1>分析ページ</h1>
          <label>パスワード<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus /></label>
          {error && <p className="analytics-error" role="alert">{error}</p>}
          <button className="btn btn-primary" type="submit">開く</button>
        </form>
      </main>
    );
  }

  return (
    <main className="analytics-screen">
      <header><p className="analytics-eyebrow">PRIVATE ANALYTICS</p><h1>プレイ分析</h1></header>
      <section className="analytics-card" aria-labelledby="analytics-period-heading">
        <h2 id="analytics-period-heading">集計期間（日本時間）</h2>
        <p className="analytics-card-note">期間を選ぶと、下のすべてのグラフ・ステージ別件数・マップをその期間で集計します。</p>
        <div className="analytics-presets" aria-label="集計期間を選択">
          {PERIOD_PRESETS.map(({ days, label, purpose }) => (
            <button type="button" key={days} aria-pressed={activePreset === days} onClick={() => applyPreset(days)} disabled={loading}>
              <strong>{label}</strong><span>{purpose}</span>
            </button>
          ))}
        </div>
        <p className="analytics-period-status" role="status">
          {loading ? "集計中…" : summary ? `表示中：${displayDate(summary.from)} 〜 ${displayDate(summary.to)}` : "集計データを取得しています…"}
        </p>
        <details className="analytics-custom-period">
          <summary>日時を指定して集計</summary>
          <form className="analytics-period" onSubmit={(event) => { event.preventDefault(); void load(); }}>
            <label>開始日時<input type="datetime-local" value={from} onChange={(event) => setFrom(event.target.value)} disabled={loading} /></label>
            <label>終了日時<input type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} disabled={loading} /></label>
            <button className="btn btn-primary" type="submit" disabled={loading}>{loading ? "集計中…" : "この期間で集計"}</button>
          </form>
        </details>
      </section>
      {error && <p className="analytics-error" role="alert">{error}</p>}
      {summary && <>
        <section className="analytics-card"><div className="analytics-chart-heading"><div><h2>日別プレイ</h2><p className="analytics-card-note">7日間・30日間を選ぶと、日ごとの推移を確認できます。棒をタップすると内訳を表示します。</p></div><div className="analytics-segmented">{(["stages", "players"] as const).map((item) => <button type="button" className={dailyMetric === item ? "is-selected" : ""} onClick={() => setDailyMetric(item)} key={item}>{item === "stages" ? "ステージ別" : "ユニークユーザー"}</button>)}</div></div><DailyChart from={summary.from} to={summary.to} stageRows={summary.daily} playerRows={summary.dailyPlayers} metric={dailyMetric} /></section>
        <section className="analytics-card"><div className="analytics-chart-heading"><h2>時間別プレイ</h2><div className="analytics-segmented">{(["stages", "players"] as const).map((item) => <button type="button" className={hourlyMetric === item ? "is-selected" : ""} onClick={() => setHourlyMetric(item)} key={item}>{item === "stages" ? "ステージ別" : "ユニークユーザー"}</button>)}</div></div><HourlyChart from={summary.from} to={summary.to} stageRows={summary.hourly} playerRows={summary.hourlyPlayers} metric={hourlyMetric} /></section>
        <section className="analytics-card"><h2>ステージ別ラン開始</h2><div className="analytics-stage-list">{listStages().map((stage) => <button type="button" className={stage.id === stageId ? "is-selected" : ""} onClick={() => setStageId(stage.id)} key={stage.id}><span>{stage.name}</span><b>{summary.stages.find((row) => row.stageId === stage.id)?.runCount ?? 0}</b></button>)}</div></section>
        <section className="analytics-card"><div className="analytics-map-heading"><h2>{listStages().find((stage) => stage.id === stageId)?.name}：マップ分析</h2><div className="analytics-segmented">{(["dropped", "gaveUp", "total"] as const).map((item) =><button type="button" className={metric === item ? "is-selected" : ""} onClick={() => setMetric(item)} key={item}>{METRIC_LABELS[item]}</button>)}</div></div><AnalyticsMap stageId={stageId} nodes={summary.nodes} metric={metric} /></section>
      </>}
    </main>
  );
}
